import { NavigationItemObject } from "../shared/NavigationItemObject";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  isActionGroup,
  isDivider,
  isSpacer,
  ActionGroup,
} from "shared-ts-ui-action";
import { isComponent } from "../shared/Component";
import {
  useResizeDetector,
  UseResizeDetectorReturn,
} from "react-resize-detector";

export interface UseOverflowItems {
  visibleItems: Array<NavigationItemObject>;
  overflowedItems: Array<NavigationItemObject>;
  ref: UseResizeDetectorReturn<HTMLDivElement>["ref"];
}

export const useResolveVisibleAndOverflowItems = (
  items: Array<NavigationItemObject>
): UseOverflowItems => {
  const { width, ref } = useResizeDetector<HTMLDivElement>({
    handleHeight: false,
    handleWidth: true,
  });

  const displayableItems = useMemo(
    () => filterDisplayableItems(items),
    [items]
  );

  const [visibleCount, setVisibleCount] = useState(displayableItems.length);

  const visibleItems = useMemo(() => {
    // Slice the array from the beginning up to visibleItemsCount
    return displayableItems.slice(0, visibleCount);
  }, [displayableItems, visibleCount]);

  const overflowedItems = useMemo(() => {
    // Slice the array from visibleItemsCount to the end
    return displayableItems.slice(visibleCount).map((item) => {
      if (item !== undefined && isActionGroup(item)) {
        return {
          ...item,
          preferences: {
            displayInline: true,
          },
        } as ActionGroup;
      }
      return item;
    });
  }, [displayableItems, visibleCount]);

  const increaseVisible = useCallback((value?: number) => {
    setVisibleCount((prev) => prev + (value ?? 1));
  }, []);

  const reduceVisible = useCallback((value?: number) => {
    setVisibleCount((prev) => prev - (value ?? 1));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (el != null) {
      const overFlowInPixels = el.scrollWidth - el.clientWidth;

      let childrenWidth = 0;
      let childWidth = 0;
      for (const child of el.children) {
        childrenWidth += child.clientWidth;
        if (child.clientWidth > childWidth) {
          childWidth = child.clientWidth;
        }
      }

      if (overFlowInPixels > 0) {
        if (visibleItems.length > 1) {
          reduceVisible();
        }
      } else if (
        childrenWidth + childWidth <= el.clientWidth &&
        overflowedItems.length > 0
      ) {
        increaseVisible();
      }
    }
  }, [
    width,
    visibleItems,
    overflowedItems,
    ref,
    reduceVisible,
    increaseVisible,
  ]);

  return {
    visibleItems,
    overflowedItems,
    ref: ref,
  };
};

const filterDisplayableItems = (
  items: Array<NavigationItemObject>
): Array<NavigationItemObject> => {
  return items.filter((item) => {
    if (item == null) {
      return false;
    } else if (isSpacer(item)) {
      return false;
    } else if (isDivider(item)) {
      return false;
    } else if (isComponent(item)) {
      return false;
    } else if (React.isValidElement(item)) {
      return false;
    }
    return true;
  });
};
